// Entries the owner makes in Canopy: labor, cash costs (manual expenses) and added revenue.
// They live in three tables (labor_entries, manual_expenses, manual_revenue), each row owned by one business
// (contractor_id). This file is the pure part (row shapes, money, totals) plus saving through a passed-in
// client so it can be tested without a database.
//
// Money rules (hard rule 5):
//   - Everything is whole cents. Each row is rounded to whole cents once, rows are added as integers,
//     and dollars appear only at the end.
//   - Labor with hours and a rate: hours and rate are first rounded to 2 decimals (the size the database
//     keeps), then hours x rate is rounded ONCE to whole cents (half a cent rounds up). The rounded line is
//     the saved amount, so adding lines never drifts. Done with whole-number math, no floating-point error.
//   - Each QuickBooks cost line is labor OR materials (decided by labor recognition). Canopy-entered labor
//     is a separate list, added on top of the QuickBooks labor. Each Canopy-entered cash cost is labor OR materials by the
//     same decision as QuickBooks costs (the vendor choice, then the category choice, then the keyword default; see
//     src/laborRules.js), so "Subcontractor" counts as labor unless the owner says otherwise.
//     Added revenue adds to revenue. Nothing is counted in two places because QuickBooks lines and Canopy
//     entries come from different tables.
import { toWholeCents } from "./money";
import { marginPercent } from "./aiCharts";
import { buildLaborLookup, classifyExpense } from "./laborRules";
import { isMissingTable, isMissingColumn } from "./lib/dbErrors";

export const LABOR_NOTE = "Use this for labor that is not already in QuickBooks. Labor that came from QuickBooks is already counted, so adding it here would count it twice.";

export const SAVE_ERROR_MESSAGE = "We couldn't save that entry, so nothing was added. Check your connection and try again.";
export const NOT_SET_UP_MESSAGE = "Saving entries isn't set up for your account yet, so nothing was added. We've been told, and it will work once the update is applied.";
export const DELETE_ERROR_MESSAGE = "We couldn't remove that entry, so it is still there. Try again.";

export const KINDS = {
  labor:   { table: "labor_entries",   list: "laborEntries" },
  expense: { table: "manual_expenses", list: "manualExpenses" },
  revenue: { table: "manual_revenue",  list: "manualRevenue" },
};

const to2 = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? toWholeCents(v) / 100 : null;
};

// Hours x rate in whole cents, with whole-number math. Both are rounded to 2 decimals first.
// 7.25 h x $18.35 = 133.0375, saved as $133.04. Returns dollars, or null if either is not a positive number.
export function laborLineAmount(hours, rate) {
  const h = Number(hours), r = Number(rate);
  if (!Number.isFinite(h) || !Number.isFinite(r) || h <= 0 || r <= 0) return null;
  const hc = toWholeCents(h), rc = toWholeCents(r);           // hundredths of an hour, cents per hour
  const cents = Math.floor((hc * rc + 50) / 100);             // hundredths x cents/hour = hundredths of a cent; round half up once
  return cents / 100;
}

// Database row -> the shape the screens use (matches the demo data).
export function rowToLabor(r) {
  return {
    id: r.id, jobId: r.job_id, description: r.description || "", workerName: r.worker_name || null,
    hours: r.hours == null ? null : Number(r.hours), hourlyRate: r.hourly_rate == null ? null : Number(r.hourly_rate),
    amount: to2(r.amount || 0), workDate: r.work_date || null, source: r.source || "manual",
  };
}
export function rowToExpense(r) {
  return {
    id: r.id, jobId: r.job_id || null, description: r.description || "", vendor: r.vendor || null,
    amount: to2(r.amount || 0), category: r.category || "other", expenseDate: r.expense_date || null, source: r.source || "manual",
  };
}
export function rowToRevenue(r) {
  return {
    id: r.id, jobId: r.job_id, description: r.description || "", amount: to2(r.amount || 0),
    paymentMethod: r.payment_method || null, revenueDate: r.revenue_date || null, source: r.source || "manual",
  };
}
const ROW_TO_ENTRY = { labor: rowToLabor, expense: rowToExpense, revenue: rowToRevenue };
export function rowToEntry(kind, row) { return ROW_TO_ENTRY[kind](row); }

// Screen entry -> database row for this business. The amount is re-worked here, never trusted from the form:
// labor with hours and a rate is hours x rate rounded once; every amount is whole cents.
export function entryToRow(kind, entry, userId) {
  const base = { contractor_id: userId, job_id: entry.jobId || null, description: entry.description || "", source: "manual" };
  if (kind === "labor") {
    const hasHoursRate = entry.hours != null && entry.hourlyRate != null && entry.hours !== "" && entry.hourlyRate !== "";
    const amount = hasHoursRate ? laborLineAmount(entry.hours, entry.hourlyRate) : to2(entry.amount);
    return { ...base, worker_name: entry.workerName || null, hours: hasHoursRate ? to2(entry.hours) : null,
      hourly_rate: hasHoursRate ? to2(entry.hourlyRate) : null, amount, work_date: entry.workDate || null };
  }
  if (kind === "expense") {
    return { ...base, vendor: entry.vendor || null, amount: to2(entry.amount), category: entry.category || "other", expense_date: entry.expenseDate || null };
  }
  return { ...base, amount: to2(entry.amount), payment_method: entry.paymentMethod || null, revenue_date: entry.revenueDate || null };
}

// Labor rows that came from QuickBooks time activities are already in the QuickBooks cost lines: leave them out.
export function countedLaborRows(rows) {
  return (rows || []).filter((r) => r.source !== "quickbooks");
}

export function emptyEntries() { return { labor: [], expense: [], revenue: [] }; }

// Group entries (screen shape) by the job they belong to. Older rows saved with no job belong to no job's totals
// (new entries always need a job).
function byJob(list) {
  const m = new Map();
  (list || []).forEach((e) => { if (e.jobId) { if (!m.has(e.jobId)) m.set(e.jobId, []); m.get(e.jobId).push(e); } });
  return m;
}

const byDateAsc = (field) => (a, b) => String(a[field] || "").localeCompare(String(b[field] || ""));

// One job summary plus its saved entries -> the summary with totals that include them. Pure; the input is not changed.
// Works from whole cents of the summary's own totals (QuickBooks revenue, labor, materials) and adds the entries.
// lookup: the owner's labor choices (buildLaborLookup); without it only the keyword default applies.
// Each cash cost in manualExpenses comes back with isLabor true/false so date views split it the same way.
export function foldEntriesIntoJob(job, entries, lookup = buildLaborLookup([])) {
  const labor = (entries.labor || []).slice().sort(byDateAsc("workDate"));
  const exps = (entries.expense || []).slice().sort(byDateAsc("expenseDate"))
    .map((e) => ({ ...e, isLabor: classifyExpense(e, lookup).isLabor }));
  const revs = (entries.revenue || []).slice().sort(byDateAsc("revenueDate"));
  const sum = (list) => list.reduce((s, x) => s + toWholeCents(x.amount || 0), 0);
  const revenueC = toWholeCents(job.revenue) + sum(revs);
  const laborC = toWholeCents(job.laborCost) + sum(labor) + sum(exps.filter((e) => e.isLabor));
  const materialC = toWholeCents(job.materialCost) + sum(exps.filter((e) => !e.isLabor));
  const costC = laborC + materialC;
  const vendorC = {};
  Object.keys(job.costByVendor || {}).forEach((v) => { vendorC[v] = toWholeCents(job.costByVendor[v]); });
  exps.forEach((e) => { if (e.vendor) vendorC[e.vendor] = (vendorC[e.vendor] || 0) + toWholeCents(e.amount || 0); });
  const costByVendor = {};
  Object.keys(vendorC).forEach((v) => { costByVendor[v] = vendorC[v] / 100; });
  const margin = marginPercent(revenueC - costC, revenueC);
  return {
    ...job,
    revenue: revenueC / 100, costs: costC / 100, materialCost: materialC / 100, laborCost: laborC / 100,
    profit: (revenueC - costC) / 100, marginPct: margin === null ? "0.0" : margin.toFixed(1),
    laborEntries: labor, manualExpenses: exps, manualRevenue: revs, costByVendor,
  };
}

// All jobs plus all saved entries (screen shape: { labor, expense, revenue }). Jobs with no revenue and no cost are dropped,
// as the loader always did.
export function foldEntriesIntoJobs(jobs, entries, lookup) {
  const l = byJob(entries.labor), e = byJob(entries.expense), r = byJob(entries.revenue);
  return (jobs || []).map((j) => foldEntriesIntoJob(j, { labor: l.get(j.id) || [], expense: e.get(j.id) || [], revenue: r.get(j.id) || [] }, lookup))
    .filter((j) => j.revenue > 0 || j.costs > 0);
}

// ─── Saving ──────────────────────────────────────────────────────────────────
// client is the Supabase client (passed in so this can be tested). Every call is limited to this business:
// rows are written with contractor_id = the signed-in user, and updates and deletes also filter on it
// (row-level security enforces the same in the database).
// Each returns { ok: true, entry } / { ok: true } or { ok: false, message } in plain words.

function failure(error, fallback) {
  if (isMissingTable(error) || isMissingColumn(error)) return { ok: false, message: NOT_SET_UP_MESSAGE };
  return { ok: false, message: fallback };
}

function validRow(kind, row) {
  if (!row.amount || row.amount <= 0) return false;
  if (!row.job_id) return false; // every entry belongs to a job
  return true;
}

export async function insertEntry({ client, userId, kind, entry }) {
  if (!userId || !KINDS[kind]) return { ok: false, message: SAVE_ERROR_MESSAGE };
  try {
    const row = entryToRow(kind, entry, userId);
    if (!validRow(kind, row)) return { ok: false, message: SAVE_ERROR_MESSAGE };
    const { data, error } = await client.from(KINDS[kind].table).insert(row).select().single();
    if (error || !data) return failure(error, SAVE_ERROR_MESSAGE);
    return { ok: true, entry: rowToEntry(kind, data) };
  } catch (e) {
    return failure(e, SAVE_ERROR_MESSAGE);
  }
}

export async function updateEntry({ client, userId, kind, id, entry }) {
  if (!userId || !id || !KINDS[kind]) return { ok: false, message: SAVE_ERROR_MESSAGE };
  try {
    const row = entryToRow(kind, entry, userId);
    if (!validRow(kind, row)) return { ok: false, message: SAVE_ERROR_MESSAGE };
    const { contractor_id, source, ...changes } = row; // owner and origin never change
    const { data, error } = await client.from(KINDS[kind].table).update(changes).eq("id", id).eq("contractor_id", userId).select().single();
    if (error || !data) return failure(error, SAVE_ERROR_MESSAGE);
    return { ok: true, entry: rowToEntry(kind, data) };
  } catch (e) {
    return failure(e, SAVE_ERROR_MESSAGE);
  }
}

export async function deleteEntry({ client, userId, kind, id }) {
  if (!userId || !id || !KINDS[kind]) return { ok: false, message: DELETE_ERROR_MESSAGE };
  try {
    const { data, error } = await client.from(KINDS[kind].table).delete().eq("id", id).eq("contractor_id", userId).select("id");
    if (error) return failure(error, DELETE_ERROR_MESSAGE);
    if (!Array.isArray(data) || data.length === 0) return { ok: false, message: DELETE_ERROR_MESSAGE }; // nothing was removed
    return { ok: true };
  } catch (e) {
    return failure(e, DELETE_ERROR_MESSAGE);
  }
}

// The job the owner has open, matched against the fresh job list. { job, gone }: gone is true when the job is no longer
// in the list (for example its last entry was deleted and it has no other money), so the screen must not keep showing it.
export function syncSelectedJob(selected, summaries) {
  if (!selected) return { job: null, gone: false };
  const fresh = (summaries || []).find((j) => j.id === selected.id);
  return fresh ? { job: fresh, gone: false } : { job: null, gone: true };
}

// Job Detail always shows the whole job (all time), even when the owner clicked a row that was narrowed to a period
// (for example "This month" on the dashboard). Returns the full job from the fresh list (falling back to the clicked one),
// plus `period`: the clicked row's revenue and costs when they differ from the whole job, otherwise null.
export function openWholeJob(clicked, summaries) {
  if (!clicked) return { job: null, period: null };
  const full = (summaries || []).find((j) => j.id === clicked.id) || clicked;
  const cents = (n) => Math.round((Number(n) || 0) * 100);
  const differs = full !== clicked && (cents(clicked.revenue) !== cents(full.revenue) || cents(clicked.costs) !== cents(full.costs));
  return { job: full, period: differs ? { revenue: clicked.revenue, costs: clicked.costs } : null };
}

// The one plain line shown on Job Detail when the dashboard row and the whole job differ. Null when there is nothing to say.
export function periodNoteText(period, periodLabel, formatMoney) {
  if (!period || !periodLabel) return null;
  return `Showing the whole job. ${periodLabel} on the dashboard: revenue ${formatMoney(period.revenue)}, costs ${formatMoney(period.costs)}.`;
}
